import { PlusIcon } from '@phosphor-icons/react';
import { Formik } from 'components/form/formik';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { captureException } from '@sentry/react';
import { useState } from 'react';

import { getDisplayError } from '@utils/get-display-error';
import { Button } from 'components/button/Button';
import { Card } from 'components/Card';
import { InputField } from 'components/input/InputField';
import { TrailerCostPayee } from 'src/types/enums';
import { SimpleSelectField } from 'components/select/SimpleSelectField';
import { PAID_BY_OPTIONS } from './constants';
import { parseNumberInput } from '@utils/number';
import { useTrailer } from '../trailerContext';
import { CustomerComboboxField, Customer } from 'src/app/customer/CustomerComboboxField';
import { SupplierComboboxField, Supplier } from 'src/app/supplier/SupplierComboboxField';
import { FileUploaderButton } from 'src/app/document/components/FileUploaderButton';
import { IDocument } from 'src/app/document/types';
import { createTrailerCost, type CreateTrailerCostInput } from '../../models';

const createTrailerSchema = Yup.object().shape({
  amount: Yup.string().min(1, 'Vereist').required('Vereist'),
  description: Yup.string().min(1, 'Vereist').required('Vereist'),
  date: Yup.string().required('Vereist'),
});

export const CreateTrailerCostForm = () => {
  const navigate = useNavigate();
  const { trailer } = useTrailer();
  const [trailerCostAttachment, setTrailerCostAttachment] = useState<IDocument | null>(null);

  const initialValues = {
    date: '',
    description: '',
    amount: '0',
    paidBy: PAID_BY_OPTIONS[0],
    customer: null as Customer | null,
    supplier: null as Supplier | null,
  };

  return (
    <>
      <div className="px-4">
        <div className="mb-4">
          <FileUploaderButton
            buttonText="Bijlage"
            title="Trailer kost bijlage"
            file={trailerCostAttachment}
            onSubmit={async (doc) => {
              setTrailerCostAttachment(doc);
            }}
          />
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={createTrailerSchema}
          onSubmit={async (values) => {
            try {
              const inputData: CreateTrailerCostInput = {
                trailerId: trailer.id,
                amount: parseNumberInput(values.amount, 2),
                date: values.date,
                description: values.description,
                paidBy: values.paidBy.key,
                customerId: values.customer?.id,
                supplierId: values.supplier?.id,
                documentId: trailerCostAttachment?.id || null,
              };

              await createTrailerCost(inputData);
              toast.success('Trailer kost aangemaakt');
              navigate('..');
            } catch (err: any) {
              captureException(err);
              toast.error('Kon trailer kost niet aanmaken: ' + getDisplayError(err));
            }
          }}
        >
          {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
            <form onSubmit={handleSubmit} className="grid gap-4">
              <Card title="Algemeen">
                <InputField labelText="Datum" type="date" name="date" />
                <InputField labelText="Beschrijving" type="text" name="description" />
                <InputField labelText="Bedrag" type="number" name="amount" step="0.01" />
              </Card>

              <Card title="Betaler">
                <SimpleSelectField labelText="Betaald door" items={[...PAID_BY_OPTIONS]} name="paidBy" />
                {values.paidBy.key === TrailerCostPayee.Customer && (
                  <CustomerComboboxField name="customer" isDisabled={isSubmitting} />
                )}
                {values.paidBy.key === TrailerCostPayee.Supplier && (
                  <SupplierComboboxField name="supplier" isDisabled={isSubmitting} isNullable={true} />
                )}
              </Card>

              <div>
                <Button
                  type="submit"
                  color="primary"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  iconLeft={<PlusIcon className="button-icon" />}
                >
                  Maak trailer kost aan
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </>
  );
};
